import { type JanusCtx, type Row, activeStatuses, ec, ecId, ecLean, record, scalar, upper } from '../ability/context.js';
import type { AbilityUseReaders } from '../actions/ability-use.js';
import { removeStoredStatus } from '../ability/statuses/status-effect-removal-helpers.js';
import { applyStatusEffect } from '../ability/statuses/apply-status-effect.js';
import { statusItemId, statusRemovalBefore } from '../statuses/expiry.js';
import { inventoryField, encounterField, posCoord } from '../../shared/array-types.js';
import { removeMonsterBits, type MonsterBitRemoval } from '../inventory/monster-bit-removal.js';
import type { ResourceValues } from './resource-adjust.js';
import { resolveLandingSupport } from '../statuses/removal-support.js';
import { fallingShapeSupportFloor, type RestShape } from '../inventory/floor-move.js';
import { requireInventoryFloor } from '../compile/inventory-facet.js';
import { readInventoryRows } from '../compile/inventory-raw-readers.js';

const PRONE = 'F5ACBDB4-D923-3684-57F6-FBF3F720AEFA';
const CRIPPLED = '99C862B9-D6AE-4E18-7803-A6F9B42A88E1';
const INTEGRATED = '36C061BF-E480-4334-961C-26500150E3C4';
export const RECOVERY_STATUS_IDS: readonly string[] = ['62E83DEB-4569-AE3A-F671-FF514C508FD2', '8AE129AF-52D8-CCE8-706A-85767DB52740',
  '553734EC-33D3-4527-AB2E-EF48ACED8DF0', '9AA7588A-4575-9550-343D-6ADB0C74C17F',
  'EE063289-1AF3-41CF-B73C-64A0556E6D07', '2EAAA2E8-1B9C-196D-3E58-C29C09FF8492'];

export interface ConsumedRecoveryBody {
  inventory_id: string; target: string;
  before: { inventory_deleted: number; item_qty: number; inventory_encounter_creature_id: string };
  contents?: { inventory_id: string }[];
}
export interface RecoveryChanges {
  monsterBitRemovals: MonsterBitRemoval[];
  bodies: ConsumedRecoveryBody[];
}
export type RecoveryReaders = AbilityUseReaders & { geometry?: (row: Row) => { diameter: number } | null };

/** The resource tools strip these states even when V is adjusted down to zero. A positive adjustment of a dead row revives it. */
export function planResourceRecovery(ctx: JanusCtx, actorId: string, values: ResourceValues, held: ReadonlyMap<string, Row>,
  read: RecoveryReaders): RecoveryChanges {
  const target = ctx.members.get(upper(actorId))!;
  const revive = Number(scalar(ecLean(target, 'encounter_creature_dead')) ?? 0) === 1 && Number(values.v_current) > 0;
  // A pending floor is unknown, never an empty corpse search.
  if (revive) requireInventoryFloor(ctx.encounter);
  ec(target).v_current = values.v_current;
  const vitality = record(record(target.computed)?.vitality);
  if (vitality) Object.assign(vitality, { v_current: values.v_current, base_v_current: values.v_current });
  const remove = (id: string): void => {
    for (const status of activeStatuses(ctx, target)) if (statusItemId(status) === id
      && activeStatuses(ctx, target).some(held => upper(scalar(held.status_id)) === upper(scalar(status.status_id)))) {
      const before = statusRemovalBefore(status, ecId(target));
      const first = ctx.statusesRemoved.length;
      removeStoredStatus(ctx, target, status, 'healing', { suppressCorpseDecay: revive });
      for (const entry of ctx.statusesRemoved.slice(first)) if (upper(entry.status_id) === upper(scalar(status.status_id))) entry.before = before;
    }
  };
  for (const id of RECOVERY_STATUS_IDS) remove(id);
  if (activeStatuses(ctx, target).some(status => statusItemId(status) === CRIPPLED)) {
    remove(CRIPPLED);
    if (!revive) applyStatusEffect(ctx, { abilityId: PRONE, casterId: ecId(target), targetIds: [ecId(target)], rawDiceMod: 0,
      rawDiceAdvDisStacks: 0, useAbilityVariable1: null, useAbilityVariable2: null, castingTargets: '' }, target, PRONE,
    'status_effect_applied', target, target, { ends: '0-0-0-0-0-0', flags: [0, 0, 0, 0] }, true, true, 0, false, null);
  }
  let bodies: ConsumedRecoveryBody[] = [];
  if (revive) {
    remove(PRONE);
    const base = ctx.read;
    ctx.read = { ...base, entries: (row, key) => base.entries(row, key).filter(entry => key !== 'inventory'
      || (record(entry) && base.statuses(entry as Row).some(status => statusItemId(status) === INTEGRATED))) };
    try { removeMonsterBits(ctx, target); } finally { ctx.read = base; }
    bodies = planRecoveryBodies(ctx, actorId, held, read);
  }
  // Loss hooks see the still-dead state; support settles at the consumed body.
  if (revive) {
    ec(target).encounter_creature_dead = 0;
    ec(target).encounter_creature_deleted = 0;
    ec(target).v_current = Math.max(1, Number(record(record(target.computed)?.vitality)?.v_current ?? scalar(ecLean(target, 'v_current')) ?? 0));
    const rawShape = encounterField(ctx.encounter, 'encounter_shape');
    if (bodies.length && rawShape != null) {
      const shape = record(typeof rawShape === 'string' ? JSON.parse(rawShape) : rawShape) as RestShape | null;
      const z = posCoord(target, 'z_pos') ?? 0;
      const floor = shape ? fallingShapeSupportFloor(shape, posCoord(target, 'x_pos') ?? 0, posCoord(target, 'y_pos') ?? 0, z, 0.05) : null;
      if (floor === null || Math.abs(z - floor) > 0.05) resolveLandingSupport(ctx, ecId(target));
    }
  }
  return { monsterBitRemovals: ctx.monsterBitRemovals ?? [], bodies };
}

/** Shared exact-body cleanup also repairs a revive whose creature arrived before
 * its corpse. A living creature keeps its current position and statuses. */
export function planRecoveryBodies(ctx: JanusCtx, actorId: string, held: ReadonlyMap<string, Row>, read: RecoveryReaders,
  relocate = true): ConsumedRecoveryBody[] {
    requireInventoryFloor(ctx.encounter);
    const target = ctx.members.get(upper(actorId))!;
    const bodies: ConsumedRecoveryBody[] = [];
    const eid = upper(encounterField(ctx.encounter, 'encounter_id'));
    for (const [id, body] of held) {
      if (upper(inventoryField(body, 'inventory_encounter_creature_id')) !== ecId(target) || Number(inventoryField(body, 'inventory_deleted') ?? 0) !== 0) continue;
      const contents: NonNullable<ConsumedRecoveryBody['contents']> = [];
      for (const [inventory_id, child] of held) if (upper(inventoryField(child, 'possess_id')) === id
        && Number(inventoryField(child, 'inventory_deleted') ?? 0) === 0) contents.push({ inventory_id });
      bodies.push({ inventory_id: id, target: ecId(target), before: { inventory_deleted: 0,
        item_qty: Number(inventoryField(body, 'item_qty') ?? 1), inventory_encounter_creature_id: ecId(target) },
        ...(contents.length ? { contents } : {}) });
      // Ordinary gear already belongs to the creature (the corpse proxies it).
      // Only direct physical contents need a new parent; their own trees stay intact.
      for (const child of contents) Object.assign(record(held.get(child.inventory_id)!.inventory)!, { possess_id: ecId(target) });
      if (contents.length) {
        target.inventory = [...readInventoryRows(target, 'inventory'), ...contents.map(child => held.get(child.inventory_id)!)];
        body.inventory_in_container = readInventoryRows(body, 'inventory_in_container')
          .filter(child => !contents.some(moved => moved.inventory_id === upper(inventoryField(child, 'inventory_id'))));
      }
      let at = body, holder = upper(inventoryField(body, 'possess_id'));
      for (let hop = 0; hop < 8 && holder && holder !== eid; hop++) {
        const parent = ctx.members.get(holder) ?? held.get(holder);
        if (!parent) break;
        at = parent;
        if (ctx.members.has(holder)) break;
        holder = upper(inventoryField(parent, 'possess_id'));
      }
      if (relocate && encounterField(ctx.encounter, 'encounter_shape') != null) {
        const coordinate = (axis: 'x_pos' | 'y_pos' | 'z_pos'): number => posCoord(at, axis)
          ?? Number(ecId(at) ? ecLean(at, axis) ?? 0 : inventoryField(at, axis) ?? 0);
        let x = coordinate('x_pos');
        if (upper(inventoryField(body, 'possess_id')) !== eid)
          x += (read.geometry?.(at)?.diameter ?? 5) / 2 + (read.geometry?.(target)?.diameter ?? 5) / 2 + 1;
        const position = { x_pos: x, y_pos: coordinate('y_pos'), z_pos: coordinate('z_pos') };
        Object.assign(ec(target), position);
        target.position = { ...record(target.position), ...position };
      }
      Object.assign(record(body.inventory) ?? body, { inventory_deleted: 1, item_qty: 0, inventory_encounter_creature_id: '' });
    }
    return bodies;
}

